import React, { useState } from "react";
import { MailCheck, Loader2, RefreshCw } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { useLang } from "../../context/LanguageContext";

export const VerifyEmailBanner = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");

  if (!user || user.role !== "business" || user.emailVerified) return null;

  const resend = async () => {
    setBusy("send");
    try {
      await api.post("/auth/verify-email/send");
      toast({ title: t("ver.sent"), description: t("ver.sentNote", { email: user.email }) });
    } catch (err) {
      toast({ title: t("ver.couldNotSend"), description: err.response?.data?.detail || "" });
    } finally { setBusy(""); }
  };

  const confirm = async () => {
    setBusy("code");
    try {
      await api.post("/auth/verify-email/confirm", { code: code.trim() });
      await refresh();
      toast({ title: t("ver.done"), description: t("ver.doneNote") });
    } catch (err) {
      toast({ title: t("ver.badCode"), description: err.response?.data?.detail || "" });
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border border-[#f5b942]/50 bg-[#fdf6e6] dark:bg-[#33280c] p-4 sm:p-5" data-testid="verify-email-banner">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 shrink-0 rounded-xl bg-[#f5b942]/25 flex items-center justify-center">
          <MailCheck size={17} className="text-[#b45309]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-[#7c5010] dark:text-[#f5b942]">{t("ver.title")}</p>
          <p className="text-sm text-[#7c5010]/85 dark:text-[#f5b942]/80 mt-0.5">
            {t("ver.note", { email: user.email })}
          </p>
          <p className="text-xs text-[#7c5010]/70 dark:text-[#f5b942]/65 mt-1" data-testid="verify-domain-tip">
            {t("ver.domainTip")}
          </p>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder={t("ver.codePlaceholder")} data-testid="verify-code-input"
              className="w-full sm:w-40 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-bold tracking-[0.2em] text-center" />
            <button onClick={confirm} disabled={code.length !== 6 || !!busy} data-testid="verify-code-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-bold px-4 py-2.5 transition-colors">
              {busy === "code" && <Loader2 className="animate-spin" size={15} />} {t("ver.confirmCode")}
            </button>
            <button onClick={resend} disabled={!!busy} data-testid="verify-resend-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#b45309]/40 text-[#b45309] dark:text-[#f5b942] hover:bg-[#f5b942]/15 disabled:opacity-60 text-sm font-bold px-4 py-2.5 transition-colors">
              {busy === "send" ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={14} />} {t("ver.resend")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailBanner;
